import { useRef, useState } from 'react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { imgSrcSet } from 'Guide/image_srcset';
import { ImageLightbox } from 'Shared/ImageLightbox';
import type { StepImage } from './types';

/**
 * A step's image media: one large image with a thumbnail row that switches it on
 * hover (or tap), and click-to-zoom into the shared ImageLightbox. Parity with
 * the image branch of embed.v5.steps.js — the vanilla hover-switch, minus the
 * FLIP transform (a plain lightbox reaches the same enlarged view).
 */
export function StepImageGallery({
   images,
   onImageChange,
}: {
   images: StepImage[];
   // Notified when the shown image changes (drives the FixBot bridge). Fires only
   // on an actual change, not for the initial image — that's reported on step
   // entry via the bridge's stepChange.
   onImageChange?: (imageIndex: number) => void;
}) {
   const [active, setActive] = useState(0);
   // The rendition the browser actually picked from the set below, read
   // when the reader opens the lightbox: src is only the fallback, and on
   // most viewports the browser takes something smaller.
   const shown = useRef<HTMLImageElement>(null);
   const [zoomed, setZoomed] = useState<{ placeholder?: string } | null>(null);

   const current = images[active] ?? images[0];
   const large = imgSrcSet(current, '50vw', 'large');

   const selectImage = (index: number) => {
      if (index === active) {
         return;
      }
      setActive(index);
      onImageChange?.(index);
   };

   return (
      <div className="text-center">
         <button
            type="button"
            onClick={() => setZoomed({ placeholder: shown.current?.currentSrc })}
            className="group relative block w-full cursor-zoom-in overflow-hidden rounded-2xl border border-gray-300"
            aria-label={_js('Zoom image')}
         >
            <img
               ref={shown}
               className="mx-auto block h-auto max-h-[65vh] w-full object-contain"
               src={large.src}
               srcSet={large.srcSet}
               sizes={large.sizes}
               alt=""
            />
            {/* Zoom affordance: a magnifier badge that fades in on hover. */}
            <span className="pointer-events-none absolute bottom-3 right-3 flex size-9 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100">
               <FaIcon name="magnifying-glass-plus" aria-hidden />
            </span>
         </button>

         {images.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-3">
               {images.map((img, i) => {
                  const thumb = imgSrcSet(img, '20vw', 'standard');
                  return (
                     <button
                        type="button"
                        key={img.id ?? i}
                        onMouseEnter={() => selectImage(i)}
                        onFocus={() => selectImage(i)}
                        onClick={() => selectImage(i)}
                        className={cn(
                           // A 2px edge in both states: at 1px the selected thumb
                           // vanishes against a busy photo, and an equal width in
                           // both states keeps the row from reflowing on select.
                           'w-[calc((100%-1.5rem)/3)] overflow-hidden rounded-2xl border-2 transition-colors',
                           i === active ? 'border-gray-500' : 'border-gray-300'
                        )}
                        aria-label={_js('Show image %1', String(i + 1))}
                        aria-pressed={i === active}
                     >
                        <img
                           className="h-auto w-full object-cover"
                           src={thumb.src}
                           srcSet={thumb.srcSet}
                           sizes={thumb.sizes}
                           alt=""
                        />
                     </button>
                  );
               })}
            </div>
         )}

         {zoomed && (
            <StepImageLightbox
               image={current}
               placeholder={zoomed.placeholder}
               onClose={() => setZoomed(null)}
            />
         )}
      </div>
   );
}

function StepImageLightbox({
   image,
   placeholder,
   onClose,
}: {
   image: StepImage;
   placeholder?: string;
   onClose: () => void;
}) {
   const full = imgSrcSet(image, '95vw', 'huge');
   if (!full.src) {
      return null;
   }
   return (
      <ImageLightbox
         url={full.src}
         srcSet={full.srcSet}
         sizes={full.sizes}
         label={_js('Step image')}
         placeholder={placeholder}
         onClose={onClose}
      />
   );
}
